import { TOUR_ANCHOR_ATTR } from './types.js';
/**
 * Anchor lookup and the two questions a tour asks before it points at
 * anything: "can the user actually see this element?" and "is somebody else's
 * overlay on top of the page right now?".
 *
 * jsdom has no layout, so every answer here is computed from
 * `getBoundingClientRect`/`getClientRects`/`getComputedStyle` — which the tests
 * stub per element — and never from `offsetWidth`, which jsdom leaves at 0.
 */
/** Below this share of its box visible, an anchor needs a scroll; still below after one, it is unusable. */
export const MIN_VISIBLE_FRACTION = 0.6;
/** Smaller than this on either side is not a target a person can find. */
const MIN_ANCHOR_SIZE = 4;
/** The coachmark's own portal root; never counted as a foreign overlay. */
export const COACHMARK_ROOT_ATTR = 'data-bk-coachmark';
/** The first element carrying `data-tour="<id>"` under `root` (default: the document). */
export function findAnchor(id, root) {
    const scope = root ?? (typeof document === 'undefined' ? undefined : document);
    if (!scope)
        return null;
    const escaped = id.replace(/["\\]/g, '\\$&');
    return scope.querySelector(`[${TOUR_ANCHOR_ATTR}="${escaped}"]`);
}
function isRendered(el) {
    if (el.getClientRects().length === 0)
        return false;
    return getComputedStyle(el).visibility !== 'hidden';
}
/**
 * Open modal dialogs (antd Drawer and Modal both render
 * `role="dialog" aria-modal="true"`), excluding the coachmark's own panel.
 */
function openModalDialogs() {
    if (typeof document === 'undefined')
        return [];
    return [
        ...document.querySelectorAll('[role="dialog"][aria-modal="true"], [role="alertdialog"]'),
    ].filter((el) => !el.closest(`[${COACHMARK_ROOT_ATTR}]`) && isRendered(el));
}
function clippingOverflow(value) {
    return value !== 'visible' && value !== '';
}
/**
 * 0..1 — the share of `el`'s box left after clipping by every ancestor whose
 * `overflow-x`/`overflow-y` is not `visible` and, unless `viewport` is false,
 * by the viewport itself.
 *
 * The ancestor half is what catches rc-tabs' overflowed tabs: they are
 * translated inside an `overflow: hidden` nav, keep a perfectly normal rect,
 * and no amount of `scrollIntoView` brings them back.
 */
export function visibleFraction(el, opts = {}) {
    const rect = el.getBoundingClientRect();
    const area = rect.width * rect.height;
    if (!(area > 0))
        return 0;
    let top = rect.top;
    let left = rect.left;
    let bottom = rect.bottom;
    let right = rect.right;
    const root = el.ownerDocument.documentElement;
    const body = el.ownerDocument.body;
    for (let node = el.parentElement; node && node !== root && node !== body; node = node.parentElement) {
        const style = getComputedStyle(node);
        // The shorthand too: jsdom (and only jsdom) leaves the longhands at
        // `visible` when `overflow` alone is set.
        const [shortX = '', shortY = shortX] = (style.overflow || '').split(/\s+/);
        const clipX = clippingOverflow(style.overflowX) || clippingOverflow(shortX);
        const clipY = clippingOverflow(style.overflowY) || clippingOverflow(shortY);
        if (!clipX && !clipY)
            continue;
        const box = node.getBoundingClientRect();
        if (clipX) {
            left = Math.max(left, box.left);
            right = Math.min(right, box.right);
        }
        if (clipY) {
            top = Math.max(top, box.top);
            bottom = Math.min(bottom, box.bottom);
        }
        if (right <= left || bottom <= top)
            return 0;
    }
    if (opts.viewport !== false) {
        const view = el.ownerDocument.defaultView;
        const width = view?.innerWidth ?? root.clientWidth;
        const height = view?.innerHeight ?? root.clientHeight;
        left = Math.max(left, 0);
        top = Math.max(top, 0);
        right = Math.min(right, width);
        bottom = Math.min(bottom, height);
    }
    if (right <= left || bottom <= top)
        return 0;
    return Math.min(1, ((right - left) * (bottom - top)) / area);
}
/**
 * The full usability check. `viewport: false` ignores the viewport (the
 * target may simply need a scroll); the ancestor clipping always counts.
 */
export function anchorProblem(el, opts = {}) {
    if (!(el instanceof HTMLElement))
        return 'missing';
    if (!el.isConnected)
        return 'detached';
    if (!isRendered(el))
        return 'not-rendered';
    const rect = el.getBoundingClientRect();
    if (rect.width < MIN_ANCHOR_SIZE || rect.height < MIN_ANCHOR_SIZE)
        return 'too-small';
    if (el.closest('[aria-hidden="true"], [inert]'))
        return 'hidden-from-at';
    const modals = openModalDialogs();
    const topModal = modals[modals.length - 1];
    if (topModal && !topModal.contains(el))
        return 'behind-modal';
    if (visibleFraction(el, { viewport: false }) < MIN_VISIBLE_FRACTION)
        return 'clipped';
    if (opts.viewport !== false && visibleFraction(el) < MIN_VISIBLE_FRACTION)
        return 'off-screen';
    return null;
}
/**
 * Attached, rendered, at least 4×4, not hidden from assistive tech, inside the
 * open modal dialog when there is one, and at least 60 % visible after
 * clipping by its ancestors and the viewport.
 */
export function isAnchorUsable(el) {
    return anchorProblem(el) === null;
}
function settleScroll(reducedMotion, maxMs) {
    return new Promise((resolve) => {
        if (reducedMotion) {
            resolve();
            return;
        }
        let done = false;
        const finish = () => {
            if (done)
                return;
            done = true;
            document.removeEventListener('scrollend', finish, true);
            clearTimeout(timer);
            resolve();
        };
        // Safari/iOS has no `scrollend`: the timeout is the path it always takes.
        const timer = setTimeout(finish, maxMs);
        document.addEventListener('scrollend', finish, true);
    });
}
/**
 * Makes a usable-but-off-screen anchor visible: scrolls it to the centre
 * once, waits for the scroll to settle (`scrollend`, or `maxMs`), and answers
 * whether it is usable now. A clipped anchor (overflowed tab) is not scrolled:
 * scrolling cannot reach it.
 */
export async function prepareAnchor(el, opts = {}) {
    const problem = anchorProblem(el);
    if (problem === null)
        return true;
    if (problem !== 'off-screen')
        return false;
    const reduced = opts.reducedMotion ?? false;
    try {
        el.scrollIntoView({ block: 'center', inline: 'nearest', behavior: reduced ? 'auto' : 'smooth' });
    }
    catch {
        // Not every environment implements scrolling.
    }
    await settleScroll(reduced, opts.maxMs ?? 350);
    return isAnchorUsable(el);
}
/**
 * Resolves with the anchor once it is present and could be shown (a scroll
 * away at most), or `null` on timeout or abort. Watches DOM mutations and also
 * re-checks on a short interval, because a layout change (a drawer finishing
 * its slide) mutates nothing.
 */
export function waitForAnchor(id, opts) {
    return new Promise((resolve) => {
        if (opts.signal?.aborted || typeof document === 'undefined') {
            resolve(null);
            return;
        }
        const check = () => {
            const el = findAnchor(id, opts.root);
            return el && anchorProblem(el, { viewport: false }) === null ? el : null;
        };
        const first = check();
        if (first) {
            resolve(first);
            return;
        }
        let settled = false;
        const finish = (value) => {
            if (settled)
                return;
            settled = true;
            observer.disconnect();
            clearInterval(poll);
            clearTimeout(timeout);
            opts.signal?.removeEventListener('abort', onAbort);
            resolve(value);
        };
        const onAbort = () => finish(null);
        const tick = () => {
            const el = check();
            if (el)
                finish(el);
        };
        const observer = new MutationObserver(tick);
        observer.observe(document.body, { childList: true, subtree: true, attributes: true });
        const poll = setInterval(tick, 100);
        const timeout = setTimeout(() => finish(null), opts.timeoutMs);
        opts.signal?.addEventListener('abort', onAbort);
    });
}
/**
 * Popup CONTAINERS, not ARIA roles: the sidebar is an always-visible
 * `<ul role="menu">`, so a role-based check would block every floating tour on
 * desktop. antd portals its popups to `body`; anything inside `#root` is page
 * content.
 */
const OVERLAY_SELECTOR = [
    '[role="dialog"][aria-modal="true"]',
    '[role="alertdialog"]',
    '.ant-dropdown:not(.ant-dropdown-hidden)',
    '.ant-select-dropdown:not(.ant-select-dropdown-hidden)',
    '.ant-picker-dropdown:not(.ant-picker-dropdown-hidden)',
    '.ant-popover:not(.ant-popover-hidden)',
    '.ant-cascader-dropdown:not(.ant-select-dropdown-hidden)',
].join(', ');
/** The top-most foreign overlay (modal, drawer, open dropdown) currently open, if any. */
export function topmostOverlay() {
    if (typeof document === 'undefined')
        return null;
    const appRoot = document.getElementById('root');
    const candidates = [...document.querySelectorAll(OVERLAY_SELECTOR)].filter((el) => !(appRoot && appRoot.contains(el)) &&
        !el.closest(`[${COACHMARK_ROOT_ATTR}]`) &&
        isRendered(el));
    return candidates[candidates.length - 1] ?? null;
}
//# sourceMappingURL=anchors.js.map